import { useState } from 'preact/hooks'
import { websApi, type WebViewDto, type OnlineEventSpeakerViewDto, type WebSpeakerPatchItemDto, type UnmatchedPresenter } from '@/api/webs'
import { useToast } from '@/hooks/useToast'

export interface SpeakerEditInput {
  ContactId: string
  Name: string
  SpeakerType: string
  CompanyId?: string
  CompanyName: string
  Email: string
  Title: string
  Bio?: string
  CustomHeadshotUrl?: string | null
}

interface UseOnlineEventSpeakersParams {
  web: WebViewDto
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
}

interface UseOnlineEventSpeakersReturn {
  showAddParticipant: boolean
  setShowAddParticipant: (v: boolean) => void
  /** The BigMarker presenter the Add panel was opened for, to pre-fill it (ORB-911); null for a blank add. */
  addPrefill: UnmatchedPresenter | null
  openAddFor: (presenter: UnmatchedPresenter | null) => void
  dismissingUnmatched: boolean
  dismissUnmatchedPresenters: () => Promise<void>
  deleteTarget: OnlineEventSpeakerViewDto | null
  setDeleteTarget: (v: OnlineEventSpeakerViewDto | null) => void
  deletingParticipant: boolean
  editingSpeaker: OnlineEventSpeakerViewDto | null
  showEditModal: boolean
  setShowEditModal: (v: boolean) => void
  openEdit: (sp: OnlineEventSpeakerViewDto) => void
  saveEditedSpeaker: (upd: SpeakerEditInput) => Promise<void>
  addSpeakerAndPersist: (spk: WebSpeakerPatchItemDto) => Promise<void>
  deleteSpeakerAndPersist: (contactId: string) => Promise<boolean>
  handleSaveReorder: (patchItems: WebSpeakerPatchItemDto[]) => Promise<boolean>
}

export function useOnlineEventSpeakers({
  web,
  onPatchWeb,
}: UseOnlineEventSpeakersParams): UseOnlineEventSpeakersReturn {
  const { showSuccess, showError } = useToast()
  const [showAddParticipant, setShowAddParticipant] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<OnlineEventSpeakerViewDto | null>(null)
  const [deletingParticipant, setDeletingParticipant] = useState(false)
  const [editingSpeaker, setEditingSpeaker] = useState<OnlineEventSpeakerViewDto | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [addPrefill, setAddPrefill] = useState<UnmatchedPresenter | null>(null)
  const [dismissingUnmatched, setDismissingUnmatched] = useState(false)

  const openAddFor = (presenter: UnmatchedPresenter | null) => {
    setAddPrefill(presenter)
    setShowAddParticipant(true)
  }

  const dismissUnmatchedPresenters = async () => {
    setDismissingUnmatched(true)
    const res = await websApi.dismissUnmatchedPresenters(web.WorkspaceId, web.Id)
    setDismissingUnmatched(false)
    if (!res.error && res.data) onPatchWeb(prev => ({ ...prev, ...res.data }))
    else showError('Dismiss failed', res.error || 'Unable to dismiss the list')
  }

  const toSpeakerPatch = (speaker: OnlineEventSpeakerViewDto): WebSpeakerPatchItemDto => ({
    ContactId: speaker.ContactId || '',
    Name: speaker.Name,
    Email: speaker.Email,
    Title: speaker.Title,
    CompanyName: speaker.CompanyName,
    CompanyId: speaker.CompanyId || null,
    Bio: speaker.Bio || null,
    CustomHeadshotUrl: speaker.CustomHeadshotUrl ?? null,
    SpeakerType: speaker.SpeakerType,
  })

  const currentSpeakers = (): OnlineEventSpeakerViewDto[] =>
    (web.OnlineEvent?.Speakers || []) as OnlineEventSpeakerViewDto[]

  const openEdit = (sp: OnlineEventSpeakerViewDto) => {
    setEditingSpeaker(sp)
    setShowEditModal(true)
  }

  const saveEditedSpeaker = async (upd: SpeakerEditInput) => {
    const patched = currentSpeakers().map(s =>
      s.ContactId === upd.ContactId
        ? { ...s, Name: upd.Name, SpeakerType: upd.SpeakerType, CompanyId: upd.CompanyId || null, CompanyName: upd.CompanyName, Email: upd.Email, Title: upd.Title, Bio: upd.Bio || null, CustomHeadshotUrl: upd.CustomHeadshotUrl ?? null }
        : s
    )
    const res = await websApi.patchSpeakers(web.WorkspaceId, web.Id, web.ETag, patched.map(toSpeakerPatch))
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Participant updated', 'Changes saved.')
    } else if (res.status !== 422) {
      // A 422 is already toasted by the global validation handler, in the server's words; toasting it
      // here too showed the same failure twice, the second as raw JSON (ORB-760, ORB-1025).
      showError('Update failed', res.error || 'Unable to update participant')
    }
  }

  const addSpeakerAndPersist = async (spk: WebSpeakerPatchItemDto) => {
    const existing = currentSpeakers().map(toSpeakerPatch)
    // toSpeakerPatch keeps only the view's fields, so carry the unmatched-presenter link and a manual
    // add's LinkedIn URL (ORB-938) across by hand.
    const added = {
      ...toSpeakerPatch(spk as unknown as OnlineEventSpeakerViewDto),
      UnmatchedPresenterId: spk.UnmatchedPresenterId ?? null,
      LinkedInUrl: spk.LinkedInUrl ?? null,
    }
    const res = await websApi.patchSpeakers(web.WorkspaceId, web.Id, web.ETag, [...existing, added])
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Participant added', 'Speaker list updated.')
    } else if (res.status !== 422) {
      showError('Add failed', res.error || 'Unable to add participant')
    }
  }

  const deleteSpeakerAndPersist = async (contactId: string) => {
    setDeletingParticipant(true)
    const remaining = currentSpeakers()
      .filter(s => (s.ContactId || '') !== contactId)
      .map(toSpeakerPatch)
    const res = await websApi.patchSpeakers(web.WorkspaceId, web.Id, web.ETag, remaining)
    setDeletingParticipant(false)
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Participant removed', 'Speaker list updated.')
      return true
    }
    if (res.status !== 422) showError('Delete failed', res.error || 'Unable to remove participant')
    return false
  }

  const handleSaveReorder = async (patchItems: WebSpeakerPatchItemDto[]): Promise<boolean> => {
    const res = await websApi.patchSpeakers(web.WorkspaceId, web.Id, web.ETag, patchItems)
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Order saved', 'Speaker order updated.')
      return true
    } else if (res.status !== 422) showError('Save failed', res.error || 'Unable to reorder speakers')
    return false
  }

  return {
    showAddParticipant,
    setShowAddParticipant,
    addPrefill,
    openAddFor,
    dismissingUnmatched,
    dismissUnmatchedPresenters,
    deleteTarget,
    setDeleteTarget,
    deletingParticipant,
    editingSpeaker,
    showEditModal,
    setShowEditModal,
    openEdit,
    saveEditedSpeaker,
    addSpeakerAndPersist,
    deleteSpeakerAndPersist,
    handleSaveReorder,
  }
}
